import { getCore as get, patchCore as patch } from "../../utils/httpInterceptor";

const DEFAULT_PREFERENCES = {
  lastDataAreaId: null,
  defaultDataAreaId: null,
  defaultLanguage: null,
  lastLanguage: null,
  defaultPageSize: 10,
  pageSizeByPage: {},
  gridPreferences: {},
  filterPreferences: {},
  defaultSavedFilterIds: {},
};

let cachedPreferences = null;
let inFlight = null;

const normalizePreferences = (preferences) => ({
  ...DEFAULT_PREFERENCES,
  ...(preferences || {}),
  defaultPageSize: Number(preferences?.defaultPageSize) || DEFAULT_PREFERENCES.defaultPageSize,
  pageSizeByPage: preferences?.pageSizeByPage && typeof preferences.pageSizeByPage === "object"
    ? preferences.pageSizeByPage
    : {},
  gridPreferences: preferences?.gridPreferences && typeof preferences.gridPreferences === "object"
    ? preferences.gridPreferences
    : {},
  filterPreferences: preferences?.filterPreferences && typeof preferences.filterPreferences === "object"
    ? preferences.filterPreferences
    : {},
  defaultSavedFilterIds: preferences?.defaultSavedFilterIds && typeof preferences.defaultSavedFilterIds === "object"
    ? preferences.defaultSavedFilterIds
    : {},
});

const normalizeColumnWidths = (columnWidths) => {
  if (!columnWidths || typeof columnWidths !== "object") {
    return {};
  }
  return Object.entries(columnWidths).reduce((acc, [key, value]) => {
    const width = Math.round(Number(value));
    if (key && Number.isFinite(width) && width >= 40 && width <= 2000) {
      acc[key] = width;
    }
    return acc;
  }, {});
};

class UserPreferencesService {
  static async get({ force = false } = {}) {
    if (!force && cachedPreferences) {
      return cachedPreferences;
    }
    if (!force && inFlight) {
      return inFlight;
    }

    inFlight = get("/api/me/preferences")
      .then((response) => {
        cachedPreferences = normalizePreferences(response);
        return cachedPreferences;
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  static async update(patchPayload = {}) {
    const response = await patch("/api/me/preferences", patchPayload);
    cachedPreferences = normalizePreferences(response);
    window.dispatchEvent(new CustomEvent("userPreferencesUpdated", { detail: cachedPreferences }));
    return cachedPreferences;
  }

  static async rememberDataArea(dataAreaId) {
    if (!dataAreaId) {
      return cachedPreferences || DEFAULT_PREFERENCES;
    }
    const current = cachedPreferences || await this.get().catch(() => DEFAULT_PREFERENCES);
    return this.update({ lastDataAreaId: dataAreaId }).catch(() => current);
  }

  static async rememberLanguage(language) {
    if (!language) {
      return cachedPreferences || DEFAULT_PREFERENCES;
    }
    const current = cachedPreferences || await this.get().catch(() => DEFAULT_PREFERENCES);
    return this.update({ lastLanguage: language }).catch(() => current);
  }

  static async rememberPageSize(pageKey, pageSize) {
    if (!pageKey || !pageSize) {
      return cachedPreferences || DEFAULT_PREFERENCES;
    }
    return this.update({
      pageSizeByPage: {
        [pageKey]: Number(pageSize),
      },
    }).catch(() => cachedPreferences || DEFAULT_PREFERENCES);
  }

  static resolvePageSize(preferences, pageKey) {
    const normalized = normalizePreferences(preferences);
    const pageSpecific = pageKey ? Number(normalized.pageSizeByPage?.[pageKey]) : 0;
    return pageSpecific || normalized.defaultPageSize || DEFAULT_PREFERENCES.defaultPageSize;
  }

  static resolveGridPreference(preferences, pageKey) {
    const normalized = normalizePreferences(preferences);
    const value = pageKey ? normalized.gridPreferences?.[pageKey] : null;
    return {
      columnOrder: Array.isArray(value?.columnOrder) ? value.columnOrder : [],
      hiddenColumns: Array.isArray(value?.hiddenColumns) ? value.hiddenColumns : [],
      columnWidths: normalizeColumnWidths(value?.columnWidths),
      // null = el usuario no ha fijado preferencia -> el llamante debe caer
      // en el valor por defecto de configuración (table-config.yml).
      groupJoinedRows:
        value?.groupJoinedRows === undefined || value?.groupJoinedRows === null
          ? null
          : Boolean(value.groupJoinedRows),
    };
  }

  static async rememberGridPreference(pageKey, gridPreference) {
    if (!pageKey) {
      return cachedPreferences || DEFAULT_PREFERENCES;
    }
    return this.update({
      gridPreferences: {
        [pageKey]: gridPreference,
      },
    });
  }

  static async resetGridPreference(pageKey) {
    if (!pageKey) {
      return cachedPreferences || DEFAULT_PREFERENCES;
    }
    return this.update({
      gridPreferences: {
        [pageKey]: null,
      },
    });
  }

  static resolveFilterPreference(preferences, pageKey) {
    const normalized = normalizePreferences(preferences);
    const value = pageKey ? normalized.filterPreferences?.[pageKey] : null;
    return {
      fieldOrder: Array.isArray(value?.fieldOrder) ? value.fieldOrder : [],
      hiddenFields: Array.isArray(value?.hiddenFields) ? value.hiddenFields : [],
    };
  }

  static async rememberFilterPreference(pageKey, filterPreference) {
    if (!pageKey) {
      return cachedPreferences || DEFAULT_PREFERENCES;
    }
    return this.update({
      filterPreferences: {
        [pageKey]: filterPreference,
      },
    });
  }

  static async resetFilterPreference(pageKey) {
    if (!pageKey) {
      return cachedPreferences || DEFAULT_PREFERENCES;
    }
    return this.update({
      filterPreferences: {
        [pageKey]: null,
      },
    });
  }

  static resolveDefaultSavedFilterId(preferences, pageKey) {
    const normalized = normalizePreferences(preferences);
    return (pageKey && normalized.defaultSavedFilterIds?.[pageKey]) || null;
  }

  static async rememberDefaultSavedFilterId(pageKey, filterId) {
    if (!pageKey) {
      return cachedPreferences || DEFAULT_PREFERENCES;
    }
    return this.update({
      defaultSavedFilterIds: {
        [pageKey]: filterId || null,
      },
    });
  }

  static clearCache() {
    cachedPreferences = null;
    inFlight = null;
  }
}

export default UserPreferencesService;
